/**
 * Agent Tokens page — manage Personal Access Tokens (opat_) for AI agent clients.
 *
 * Users issue tokens here that AI clients (Claude Code, Claude Desktop, etc.)
 * use to authenticate with OrbitalCodex on their behalf.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { config } from '../config';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TokenMeta {
  jti: string;
  name: string;
  agent_kind: string;
  tenant: string;
  scopes: string[];
  active: boolean;
  created_at: string;
  expires_at: string | null;  // null = permanent token (no expiry)
  last_used_at: string | null;
  revoked_at: string | null;
}

interface UserOrg {
  id: string;
  name: string;
  role: string;
}

interface IssuedToken {
  jti: string;
  token: string;
  name: string;
  tenant: string;
  scopes: string[];
  expires_at: string;
}

// ─── Scope catalog ───────────────────────────────────────────────────────────

const SCOPE_GROUPS = [
  {
    label: 'Skills',
    scopes: [
      { value: 'skills:read', label: 'Read skills' },
      { value: 'skills:write', label: 'Publish / deactivate skills' },
    ],
  },
  {
    label: 'Configs',
    scopes: [
      { value: 'configs:read', label: 'Read configs' },
      { value: 'configs:write', label: 'Set / deactivate configs' },
    ],
  },
  {
    label: 'Memory',
    scopes: [
      { value: 'memory:read', label: 'Read memory' },
      { value: 'memory:write', label: 'Create / update / delete memory' },
    ],
  },
  {
    label: 'Audit',
    scopes: [{ value: 'audit:read', label: 'View audit log' }],
  },
  {
    label: 'Admin',
    scopes: [{ value: 'admin', label: 'Tenant-level administrative actions' }],
  },
];

const AGENT_KINDS = [
  { value: 'claude-code', label: 'Claude Code' },
  { value: 'claude-desktop', label: 'Claude Desktop' },
  { value: 'cursor', label: 'Cursor' },
  { value: 'custom', label: 'Custom / other' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null): string {
  if (!iso) return 'No expiry';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function isExpired(expires_at: string | null): boolean {
  if (!expires_at) return false; // permanent tokens never expire
  return new Date(expires_at) < new Date();
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function RevealModal({ token, onClose }: { token: IssuedToken; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(token.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-content-primary">Token created</h2>
          <span className="text-xs text-red-500 font-medium uppercase tracking-wide">Save this now — shown once</span>
        </div>

        <p className="text-sm text-content-secondary">
          <strong>{token.name}</strong> · {token.tenant} · expires {fmtDate(token.expires_at)}
        </p>

        <div className="bg-surface-secondary rounded-lg p-3 font-mono text-xs break-all text-content-primary">
          {token.token}
        </div>

        <div className="flex gap-3">
          <button
            onClick={copy}
            className="flex-1 py-2 rounded-lg bg-orbital-600 hover:bg-orbital-700 text-white text-sm font-medium transition-colors"
          >
            {copied ? 'Copied!' : 'Copy to clipboard'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-border text-sm text-content-secondary hover:bg-surface-secondary transition-colors"
          >
            Done
          </button>
        </div>

        <p className="text-xs text-content-tertiary">
          Scopes: {token.scopes.join(', ')}
        </p>
      </div>
    </div>
  );
}

function CreateForm({
  orgs,
  onIssued,
  onCancel,
}: {
  orgs: UserOrg[];
  onIssued: (t: IssuedToken) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [tenant, setTenant] = useState(orgs[0]?.id ?? '');
  const [scopes, setScopes] = useState<Set<string>>(new Set(['skills:read', 'configs:read', 'memory:read']));
  const [ttlDays, setTtlDays] = useState(30);
  const [agentKind, setAgentKind] = useState('claude-code');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleScope = (s: string) => {
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const submit = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!tenant) { setError('Select a tenant organization'); return; }
    if (scopes.size === 0) { setError('Select at least one scope'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch(`${config.gatewayUrl}/v1/agent-tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          tenant,
          scopes: Array.from(scopes),
          ttl_days: ttlDays,
          agent_kind: agentKind,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail ?? `Error ${resp.status}`);
      }
      const data = await resp.json();
      onIssued(data as IssuedToken);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-surface-primary rounded-xl border border-border p-6 space-y-5">
      <h2 className="text-base font-semibold text-content-primary">New Agent Token</h2>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-lg">{error}</div>
      )}

      {/* Name */}
      <div className="space-y-1">
        <label className="text-sm font-medium text-content-secondary">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Claude Code — laptop"
          className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface-secondary text-content-primary focus:outline-none focus:ring-2 focus:ring-orbital-500"
        />
      </div>

      {/* Tenant */}
      <div className="space-y-1">
        <label className="text-sm font-medium text-content-secondary">Tenant organization</label>
        <select
          value={tenant}
          onChange={(e) => setTenant(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface-secondary text-content-primary focus:outline-none focus:ring-2 focus:ring-orbital-500"
        >
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>

      {/* Agent kind */}
      <div className="space-y-1">
        <label className="text-sm font-medium text-content-secondary">Agent client</label>
        <select
          value={agentKind}
          onChange={(e) => setAgentKind(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface-secondary text-content-primary focus:outline-none focus:ring-2 focus:ring-orbital-500"
        >
          {AGENT_KINDS.map((k) => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
        </select>
      </div>

      {/* Scopes */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-content-secondary">Scopes</label>
        <div className="space-y-3">
          {SCOPE_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="text-xs font-medium text-content-tertiary uppercase tracking-wide mb-1">{group.label}</p>
              <div className="space-y-1">
                {group.scopes.map((s) => (
                  <label key={s.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scopes.has(s.value)}
                      onChange={() => toggleScope(s.value)}
                      className="rounded border-border text-orbital-600 focus:ring-orbital-500"
                    />
                    <span className="text-sm text-content-primary">{s.label}</span>
                    <span className="text-xs text-content-tertiary font-mono">{s.value}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* TTL */}
      <div className="space-y-1">
        <label className="text-sm font-medium text-content-secondary">
          Expires in <span className="text-orbital-600 font-semibold">{ttlDays} days</span>
        </label>
        <input
          type="range"
          min={1}
          max={90}
          value={ttlDays}
          onChange={(e) => setTtlDays(Number(e.target.value))}
          className="w-full accent-orbital-600"
        />
        <div className="flex justify-between text-xs text-content-tertiary">
          <span>1 day</span>
          <span>90 days</span>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={submit}
          disabled={submitting}
          className="flex-1 py-2 rounded-lg bg-orbital-600 hover:bg-orbital-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          {submitting ? 'Issuing…' : 'Issue token'}
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 rounded-lg border border-border text-sm text-content-secondary hover:bg-surface-secondary transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AgentTokens() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [tokens, setTokens] = useState<TokenMeta[]>([]);
  const [orgs, setOrgs] = useState<UserOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [revealed, setRevealed] = useState<IssuedToken | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?return_to=/agent-tokens', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tokensResp, orgsResp] = await Promise.all([
        fetch(`${config.gatewayUrl}/v1/agent-tokens`, { credentials: 'include' }),
        fetch(`${config.gatewayUrl}/v1/access/me/organizations`, { credentials: 'include' }),
      ]);
      if (tokensResp.ok) setTokens(await tokensResp.json());
      if (orgsResp.ok) {
        const orgsData = await orgsResp.json();
        setOrgs(Array.isArray(orgsData) ? orgsData : orgsData.organizations ?? []);
      }
    } catch (e) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated]);

  const handleRevoke = async (jti: string) => {
    if (!confirm('Revoke this token? Any agent using it will immediately lose access.')) return;
    setRevoking(jti);
    try {
      const resp = await fetch(`${config.gatewayUrl}/v1/agent-tokens/${jti}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`Error ${resp.status}`);
      setTokens((prev) => prev.map((t) => t.jti === jti ? { ...t, active: false, revoked_at: new Date().toISOString() } : t));
    } catch (e) {
      setError('Failed to revoke token');
    } finally {
      setRevoking(null);
    }
  };

  const handleIssued = (t: IssuedToken) => {
    setShowCreate(false);
    setRevealed(t);
    fetchData();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading…" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Agent Tokens</h1>
          <p className="text-sm text-content-secondary mt-1">
            Long-lived tokens for AI agent clients (Claude Code, Claude Desktop, etc.) to access OrbitalCodex on your behalf.
          </p>
        </div>
        {!showCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 rounded-lg bg-orbital-600 hover:bg-orbital-700 text-white text-sm font-medium transition-colors"
          >
            New token
          </button>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg">{error}</div>
      )}

      {/* Create form */}
      {showCreate && (
        <CreateForm
          orgs={orgs}
          onIssued={handleIssued}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Token list */}
      {tokens.length === 0 && !showCreate ? (
        <div className="text-center py-16 text-content-tertiary text-sm">
          No tokens yet. Create one to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {tokens.map((t) => {
            const expired = isExpired(t.expires_at);
            const inactive = !t.active || expired;
            return (
              <div
                key={t.jti}
                className={`bg-surface-primary rounded-xl border p-4 ${inactive ? 'border-border opacity-60' : 'border-border'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-content-primary text-sm">{t.name}</span>
                      {!t.active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          Revoked
                        </span>
                      )}
                      {t.active && expired && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                          Expired
                        </span>
                      )}
                      {t.active && !expired && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-content-tertiary font-mono">{t.jti}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {t.scopes.map((s) => (
                        <span key={s} className="text-xs px-1.5 py-0.5 rounded bg-surface-secondary text-content-secondary font-mono">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="shrink-0 text-right space-y-1">
                    <p className="text-xs text-content-tertiary">
                      {AGENT_KINDS.find((k) => k.value === t.agent_kind)?.label ?? t.agent_kind}
                    </p>
                    <p className="text-xs text-content-tertiary">Expires {fmtDate(t.expires_at)}</p>
                    {t.last_used_at && (
                      <p className="text-xs text-content-tertiary">Last used {fmtDate(t.last_used_at)}</p>
                    )}
                  </div>
                </div>

                {t.active && !expired && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => handleRevoke(t.jti)}
                      disabled={revoking === t.jti}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-colors"
                    >
                      {revoking === t.jti ? 'Revoking…' : 'Revoke'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* One-time token reveal modal */}
      {revealed && (
        <RevealModal token={revealed} onClose={() => setRevealed(null)} />
      )}
    </div>
  );
}
